type RouteMeta = {
  title: string;
  description: string;
  image: string;
  imageAlt: string;
  canonicalPath: string;
  robots?: string;
};

const SITE_NAME = "Ontoligent";
const FALLBACK_ORIGIN = "https://ontoligent.io";
const DEFAULT_IMAGE = "/og-image.png";
const CAMPAIGN_IMAGE = "/og-image-campaign.png";

const BASE_TITLE = "Ontoligent";
const BASE_DESCRIPTION =
  "Enterprise Intelligence Platform for governed decisions. Versioned ontology, permission-aware retrieval, and auditable agents.";

const DEFAULT_META: RouteMeta = {
  title: "Ontoligent - Enterprise Intelligence Platform for governed AI",
  description:
    "Versioned ontology, permission-aware retrieval, auditable agents, and closed-loop actions for regulated enterprises.",
  image: DEFAULT_IMAGE,
  imageAlt: "Ontoligent platform overview",
  canonicalPath: "/",
  robots: "index,follow,max-image-preview:large",
};

const CAMPAIGN_ROUTES = new Set(["/journey", "/use-cases"]);

const ROUTE_META: Record<string, RouteMeta> = {
  "/": DEFAULT_META,
  "/welcome": DEFAULT_META,
  "/journey": {
    title: "Journey - Ontoligent",
    description:
      "See the full journey from business problem to governed, closed-loop outcomes in 12 operational stages.",
    image: CAMPAIGN_IMAGE,
    imageAlt: "Ontoligent journey campaign visual",
    canonicalPath: "/journey",
  },
  "/use-cases": {
    title: "Use Cases & ROI - Ontoligent",
    description:
      "Concrete enterprise use cases with workflow, evidence model, and conservative ROI assumptions.",
    image: CAMPAIGN_IMAGE,
    imageAlt: "Ontoligent use-cases campaign visual",
    canonicalPath: "/use-cases",
  },
  "/legal/privacy-policy": {
    title: "Privacy Policy - Ontoligent",
    description:
      "Learn how Ontoligent collects, uses, shares, and protects information.",
    image: DEFAULT_IMAGE,
    imageAlt: "Ontoligent",
    canonicalPath: "/legal/privacy-policy",
  },
  "/legal/terms-and-conditions": {
    title: "Terms and Conditions - Ontoligent",
    description:
      "Read the terms that govern access to and use of Ontoligent.",
    image: DEFAULT_IMAGE,
    imageAlt: "Ontoligent",
    canonicalPath: "/legal/terms-and-conditions",
  },
  "/docs": {
    title: "Docs - Ontoligent",
    description:
      "Concepts, quickstart, and implementation guidance for Ontoligent architecture and operations.",
    image: DEFAULT_IMAGE,
    imageAlt: "Ontoligent documentation",
    canonicalPath: "/docs",
  },
  "/howto": {
    title: "How-to - Ontoligent",
    description:
      "Step-by-step product walkthroughs for ontology, retrieval, pipelines, and governance workflows.",
    image: DEFAULT_IMAGE,
    imageAlt: "Ontoligent how-to guides",
    canonicalPath: "/howto",
  },
  "/login": {
    title: "Sign In - Ontoligent",
    description: BASE_DESCRIPTION,
    image: DEFAULT_IMAGE,
    imageAlt: "Ontoligent",
    canonicalPath: "/login",
    robots: "noindex,nofollow",
  },
  "/signup": {
    title: "Get Started - Ontoligent",
    description: BASE_DESCRIPTION,
    image: DEFAULT_IMAGE,
    imageAlt: "Ontoligent",
    canonicalPath: "/signup",
    robots: "noindex,nofollow",
  },
};

function getOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return FALLBACK_ORIGIN;
}

function upsertMeta(selector: string, attrs: Record<string, string>) {
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }
  Object.entries(attrs).forEach(([k, v]) => node!.setAttribute(k, v));
}

function upsertLink(selector: string, attrs: Record<string, string>) {
  let node = document.head.querySelector<HTMLLinkElement>(selector);
  if (!node) {
    node = document.createElement("link");
    document.head.appendChild(node);
  }
  Object.entries(attrs).forEach(([k, v]) => node!.setAttribute(k, v));
}

function setFavicons() {
  document
    .querySelectorAll('link[data-managed="brand-favicon"]')
    .forEach((n) => n.remove());

  const defs: Array<Record<string, string>> = [
    {
      rel: "icon",
      type: "image/png",
      sizes: "16x16",
      href: "/ONTOLIGENT.-logo-assets/favicons/ONTOLIGENT.-16x16.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "32x32",
      href: "/ONTOLIGENT.-logo-assets/favicons/ONTOLIGENT.-32x32.png",
    },
    {
      rel: "icon",
      type: "image/png",
      sizes: "48x48",
      href: "/ONTOLIGENT.-logo-assets/favicons/ONTOLIGENT.-48x48.png",
    },
    {
      rel: "apple-touch-icon",
      sizes: "180x180",
      href: "/ONTOLIGENT.-logo-assets/favicons/ONTOLIGENT.-180x180.png",
    },
    {
      rel: "manifest",
      href: "/site.webmanifest",
    },
  ];

  defs.forEach((entry) => {
    const node = document.createElement("link");
    Object.entries(entry).forEach(([k, v]) => node.setAttribute(k, v));
    node.setAttribute("data-managed", "brand-favicon");
    document.head.appendChild(node);
  });
}

function toAbsolute(pathOrUrl: string, origin: string) {
  if (pathOrUrl.startsWith("http://") || pathOrUrl.startsWith("https://")) {
    return pathOrUrl;
  }
  return `${origin}${pathOrUrl}`;
}

export function applyRouteSeo(pathname: string) {
  const origin = getOrigin();
  const specific = ROUTE_META[pathname];
  const isCampaign = CAMPAIGN_ROUTES.has(pathname);

  const resolved: RouteMeta = specific ?? {
    title: pathname === "/dashboard" ? "Dashboard - Ontoligent" : BASE_TITLE,
    description: BASE_DESCRIPTION,
    image: isCampaign ? CAMPAIGN_IMAGE : DEFAULT_IMAGE,
    imageAlt: "Ontoligent",
    canonicalPath: pathname || "/",
    robots: "noindex,nofollow",
  };

  const canonical = toAbsolute(resolved.canonicalPath, origin);
  const image = toAbsolute(resolved.image, origin);

  document.title = resolved.title;

  upsertMeta('meta[name="description"]', {
    name: "description",
    content: resolved.description,
  });
  upsertMeta('meta[name="robots"]', {
    name: "robots",
    content: resolved.robots ?? "index,follow,max-image-preview:large",
  });

  upsertMeta('meta[property="og:type"]', {
    property: "og:type",
    content: "website",
  });
  upsertMeta('meta[property="og:site_name"]', {
    property: "og:site_name",
    content: SITE_NAME,
  });
  upsertMeta('meta[property="og:url"]', {
    property: "og:url",
    content: canonical,
  });
  upsertMeta('meta[property="og:title"]', {
    property: "og:title",
    content: resolved.title,
  });
  upsertMeta('meta[property="og:description"]', {
    property: "og:description",
    content: resolved.description,
  });
  upsertMeta('meta[property="og:image"]', {
    property: "og:image",
    content: image,
  });
  upsertMeta('meta[property="og:image:width"]', {
    property: "og:image:width",
    content: "1200",
  });
  upsertMeta('meta[property="og:image:height"]', {
    property: "og:image:height",
    content: "630",
  });
  upsertMeta('meta[property="og:image:alt"]', {
    property: "og:image:alt",
    content: resolved.imageAlt,
  });

  upsertMeta('meta[name="twitter:card"]', {
    name: "twitter:card",
    content: "summary_large_image",
  });
  upsertMeta('meta[name="twitter:title"]', {
    name: "twitter:title",
    content: resolved.title,
  });
  upsertMeta('meta[name="twitter:description"]', {
    name: "twitter:description",
    content: resolved.description,
  });
  upsertMeta('meta[name="twitter:image"]', {
    name: "twitter:image",
    content: image,
  });
  upsertMeta('meta[name="twitter:image:alt"]', {
    name: "twitter:image:alt",
    content: resolved.imageAlt,
  });

  upsertLink('link[rel="canonical"]', {
    rel: "canonical",
    href: canonical,
  });

  setFavicons();
}
